import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../globals";
import { push } from "../navigation/v2/sales/push";
import { cursoCorto } from "../utils/cursoCorto";
import { lanzarConfeti, sonarFestejo } from "./confeti";
import { precargar, secuencia } from "./sonidos";
import { logoDe } from "../navigation/v2/sales/tableroView";
import "./festejo.css";

/**
 * Festejo a pantalla completa cuando alguien de la rueda vende (Esteban, 2026-10-14: "gamify it para motivar a las chavas").
 * Llega por wapp-push (evento "festejo") a toda la rueda del territorio, sus supervisoras y sus directoras. Trae todo lo que
 * se enseña (nombre, foto, grupo, cursos, conteos del día), así que no pide nada a la API.
 * - Uno a la vez: si llegan varios juntos, hacen fila.
 * - Se cierra solo a los 8 s, con un toque o con Esc.
 * - Con la pestaña atrás se guarda y sale al volver, si tiene menos de 20 min; más viejo ya no festeja nada.
 * - El sonido se puede apagar (se recuerda en este navegador).
 */
export interface FestejoRanking { grupo: string; n: number; }
/** Logo (ícono del canal de WappTeam), color y sonido del equipo (VentasEquipos). */
export interface FestejoEquipo { nombre: string; logo: string | null; color: string | null; sonido: string | null; }
/** Con esta venta el equipo subió en el podio del mes: a qué puesto, a quién rebasó y a dónde quedó ese. */
export interface FestejoPodio { sube: number; antes: number; rebasado: string; baja: number | null; podio: { equipo: string; mat: number }[]; }
/** La vendedora subió dentro del top 5 de asesoras del mes: a qué puesto, desde cuál (null = entró) y a quién rebasó. */
export interface FestejoPodioAsesora { sube: number; antes: number | null; rebasada: string | null; }
export interface Festejo {
	id: number;
	podioAsesora?: FestejoPodioAsesora | null;
	/** matricula (de siempre) | bombazo (pago de 6,000 o más de una vez) | meta_semana (el equipo llegó a su proyección de la semana) | meta_mes (la directora a la del mes) */
	tipo?: "matricula" | "bombazo" | "meta_semana" | "meta_mes";
	/** Proyección lograda: la cifra, lo que llevan, el período ("Semana 1 de noviembre" / "Noviembre 2026") y el papel de la persona. */
	meta?: number; logrado?: number; periodo?: string; rol?: string;
	titulo: string;                 // "Nueva Matrícula" (Honduras) / "Nueva Inscripción" (México) / "¡Bombaaaazoo!"
	equipo?: FestejoEquipo | null;
	podio?: FestejoPodio | null;
	/** Bombazo: cuánto pagó de una vez y la moneda. */
	monto?: number; moneda?: string; alumno?: string | null;
	vendedora: { id: number; nombre: string; foto: string | null };
	grupo: string;
	plantel: string;
	cursos: string[];
	clientes: number;
	reingreso?: boolean;            // se guarda, no se enseña: el reingreso de equipo cuenta como matrícula (Esteban, 2026-10-14)
	hoyElla: number;                // cuántas lleva ella hoy (con esta)
	hoyGrupo: number;               // cuántas lleva su grupo hoy
	hoyTerritorio: number;
	ranking: FestejoRanking[];      // grupos del territorio hoy, de más a menos
	en: number;                     // epoch ms
}

const DURA_MS = 8000;
const VIGENCIA_MS = 20 * 60 * 1000;
const SONIDO_KEY = "wapp.festejoSonido";

const iniciales = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const num1 = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });
const ordinal = (n: number) => n + "ª";
const sonidoOn = (): boolean => { try { return localStorage.getItem(SONIDO_KEY) !== "off"; } catch { return true; } };

/** Frase de racha según cuántas lleva hoy. */
const racha = (n: number): string | null => {
	if (n >= 5) return "¡Imparable! " + ordinal(n) + " de hoy";
	if (n === 4) return "¡En llamas! 4ª de hoy";
	if (n === 3) return "¡Triplete! 3ª de hoy";
	if (n === 2) return "¡Doblete! 2ª de hoy";
	return null;
};

export const TarjetaFestejo: React.FC<{ f: Festejo; esYo: boolean; onCerrar: () => void; sonido: boolean; onSonido: () => void; restante?: number }> = ({ f, esYo, onCerrar, sonido, onSonido, restante }) => {
	const max = Math.max(1, ...f.ranking.map((r) => r.n));
	const bombazo = f.tipo === "bombazo";
	const meta = f.tipo === "meta_semana" || f.tipo === "meta_mes";
	const frase = bombazo || meta ? null : racha(f.hoyElla);
	const col = f.equipo?.color || undefined;
	const logo = f.equipo ? logoDe({ equipo: f.equipo.nombre, logo: f.equipo.logo, color: f.equipo.color }) : null;
	const dineroDe = (v: number) => (f.moneda ? f.moneda + " " : "") + Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	return (
		<div className={"fst-tarjeta" + (bombazo ? " fst-bombazo" : "") + (meta ? " fst-meta" : "")} onClick={(e) => e.stopPropagation()} role="alertdialog" aria-labelledby="fst-titulo" aria-describedby="fst-nombre"
			style={col ? { "--fst-col": col } as React.CSSProperties : undefined}>
			<div className="fst-arriba">
				<span className="fst-sello">{f.plantel}</span>
				<button className="fst-son" onClick={onSonido} aria-label={sonido ? "Silenciar festejos" : "Activar sonido de festejos"} title={sonido ? "Silenciar" : "Con sonido"}>
					{sonido ? (
						<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a10 10 0 0 1 0 14" /></svg>
					) : (
						<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="m23 9-6 6" /><path d="m17 9 6 6" /></svg>
					)}
				</button>
				<button className="fst-x" onClick={onCerrar} aria-label="Cerrar">×</button>
			</div>
			<h1 id="fst-titulo" className="fst-titulo">{f.titulo.startsWith("¡") ? f.titulo : "¡" + f.titulo + "!"}</h1>
			<div className="fst-duo">
				<div className="fst-foto">
					<span className="fst-aro" aria-hidden="true" />
					{f.vendedora.foto ? <img src={f.vendedora.foto} alt="" /> : <span className="fst-ini">{iniciales(f.vendedora.nombre)}</span>}
					{f.clientes > 1 ? <span className="fst-multi">×{f.clientes}</span> : null}
				</div>
				{logo ? <img className="fst-logo" src={logo} alt="" /> : null}
			</div>
			<div id="fst-nombre" className="fst-nombre">{esYo ? "¡Lo lograste, " + f.vendedora.nombre.split(" ")[0] + "!" : f.vendedora.nombre}</div>
			<div className="fst-grupo">{meta ? (f.rol ? f.rol + " · " : "") + (f.tipo === "meta_mes" ? f.grupo : "Equipo " + f.grupo) : "Equipo " + f.grupo}</div>
			{meta && f.meta ? (
				<div className="fst-meta-cifra">
					<b>{num1(f.logrado || 0)}</b><span>de {num1(f.meta)}</span>
					<small>matrículas · {f.periodo}</small>
					<div className="fst-meta-barra"><i style={{ width: Math.min(100, Math.round(100 * (f.logrado || 0) / f.meta)) + "%" }} /></div>
				</div>
			) : null}
			{f.cursos.length ? (
				<div className="fst-cursos">
					{f.cursos.slice(0, 3).map((c) => <span key={c} className="fst-curso" title={c}>{cursoCorto(c) || c}</span>)}
				</div>
			) : null}
			{bombazo && f.monto ? <div className="fst-bombazo-monto">{dineroDe(f.monto)}<small>pagados de una vez{f.alumno ? " · " + f.alumno : ""}</small></div> : null}
			{frase ? <div className="fst-racha"><span aria-hidden="true">🔥</span> {frase}</div> : null}
			{f.podio || f.podioAsesora ? (
				<div className="fst-podio">
					{f.podio ? <div className="fst-podio-sube">{logo ? <img src={logo} alt="" /> : null}{f.equipo?.nombre || f.grupo} sube al {f.podio.sube}.º</div> : null}
					{f.podio?.baja ? <div className="fst-podio-baja">{f.podio.rebasado} baja al {f.podio.baja}.º</div> : null}
					{f.podioAsesora ? <div className="fst-podio-sube">{f.vendedora.nombre.split(" ")[0]} {f.podioAsesora.antes ? `sube al ${f.podioAsesora.sube}.º` : `entra al top 5 en el ${f.podioAsesora.sube}.º`} de asesoras</div> : null}
					{f.podioAsesora?.rebasada ? <div className="fst-podio-baja">{f.podioAsesora.rebasada} baja un puesto</div> : null}
				</div>
			) : null}
			{bombazo || meta ? null : f.ranking.length > 1 ? (
				<div className="fst-ranking" aria-label="Equipos hoy">
					<div className="fst-rk-cab"><span>Hoy en {f.plantel}</span><b>{f.hoyTerritorio}</b></div>
					{f.ranking.slice(0, 8).map((r, i) => (
						<div key={r.grupo} className={"fst-rk" + (r.grupo === f.grupo ? " suyo" : "") + (r.n ? "" : " cero")}>
							<span className="fst-rk-pos">{r.n ? i + 1 : "–"}</span>
							<span className="fst-rk-nom">{r.grupo}</span>
							<span className="fst-rk-barra">{r.n ? <i style={{ width: Math.max(6, (r.n / max) * 100) + "%" }} /> : null}</span>
							<span className="fst-rk-n">{r.n}</span>
						</div>
					))}
				</div>
			) : f.ranking.length === 0 ? null : (
				<div className="fst-solo">Equipo {f.grupo}: <b>{f.hoyGrupo}</b> hoy</div>
			)}
			{restante !== undefined ? <div className="fst-tiempo"><i style={{ animationDuration: DURA_MS + "ms" }} /></div> : null}
			{restante && restante > 0 ? <div className="fst-fila">+{restante} más</div> : null}
		</div>
	);
};

const FestejoMatricula: React.FC = () => {
	const campusId: number | null = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId) || null;
	const yo: number | null = useSelector((state: RootState) => state.rootReducer.sessionState.user?.id) || null;
	const [fila, setFila] = useState<Festejo[]>([]);
	const [aLaVista, setALaVista] = useState<boolean>(document.visibilityState === "visible");
	const [sonido, setSonido] = useState<boolean>(sonidoOn());
	const canvas = useRef<HTMLCanvasElement | null>(null);
	const vistos = useRef<Set<number>>(new Set());
	const actual = fila[0] || null;

	const recibir = useCallback((d: any) => {
		const f = d as Festejo;
		if (!f || !f.id || vistos.current.has(f.id)) return;
		vistos.current.add(f.id);
		precargar(f.tipo === "bombazo" ? "bombazo" : f.tipo === "meta_semana" || f.tipo === "meta_mes" ? "sube" : f.equipo?.sonido || "");
		if (f.podio || f.podioAsesora) precargar("sube");
		setFila((q) => [...q, f]);
	}, []);

	useEffect(() => {
		if (!campusId) return;
		const soltar = push.usar(campusId);
		const quitar = push.on("festejo", recibir);
		return () => { quitar(); soltar(); };
	}, [campusId, recibir]);

	const cerrar = useCallback(() => setFila((q) => q.slice(1)), []);

	useEffect(() => {
		const cambio = () => setALaVista(document.visibilityState === "visible");
		document.addEventListener("visibilitychange", cambio);
		return () => document.removeEventListener("visibilitychange", cambio);
	}, []);

	// Al mostrar uno: confeti, sonido y cuenta regresiva. Con la pestaña atrás espera; si ya es viejo, se salta.
	useEffect(() => {
		if (!actual) return;
		if (Date.now() - actual.en > VIGENCIA_MS) { cerrar(); return; }
		if (!aLaVista) return;
		const parar = canvas.current ? lanzarConfeti(canvas.current) : () => undefined;
		// El sonido del equipo (Esteban, 3-nov-2026) y, si rebasó en el podio, después su fanfarria; el Bombazo tiene el suyo. Nunca encimados.
		if (sonido) {
			const clip = actual.tipo === "bombazo" ? "bombazo" : actual.tipo === "meta_semana" || actual.tipo === "meta_mes" ? "sube" : actual.equipo?.sonido || null;
			// La fanfarria del rebase suena una vez: por el equipo o por el top 5 de asesoras (nunca por movimientos fuera del top 5).
			if (clip) secuencia([clip, (actual.podio || actual.podioAsesora) && actual.tipo !== "bombazo" ? "sube" : null]); else sonarFestejo();
		}
		const t = setTimeout(cerrar, DURA_MS);
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
		window.addEventListener("keydown", esc);
		return () => { parar(); clearTimeout(t); window.removeEventListener("keydown", esc); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actual, aLaVista, cerrar]);

	const cambiarSonido = () => {
		const v = !sonido;
		setSonido(v);
		try { localStorage.setItem(SONIDO_KEY, v ? "on" : "off"); } catch { /* nada */ }
	};

	if (!actual || !aLaVista) return null;
	return (
		<div className="fst-fondo" onClick={cerrar}>
			<canvas ref={canvas} className="fst-confeti" aria-hidden="true" />
			<TarjetaFestejo key={actual.id} f={actual} esYo={!!yo && yo === actual.vendedora.id} onCerrar={cerrar}
				sonido={sonido} onSonido={cambiarSonido} restante={fila.length - 1} />
		</div>
	);
};

export default FestejoMatricula;
